// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  Edit3,
  Filter,
  MoreVertical,
  Pin,
  UnfoldHorizontal,
  WrapText,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import type { FilterValue } from "@/components/filter-popover";
import { FilterPopover } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { getColumnCapabilities } from "@/lib/column-capabilities-constants";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";

interface ColumnActionsMenuProps {
  column: UnifiedTableColumn;
  isPinned: boolean;
  isWrapped?: boolean;
  currentFilter: FilterValue;
  updateFilter: (key: string, value: FilterValue) => void;
  clearFilter: (key: string) => void;
  onTogglePin?: (columnId: string) => void;
  onToggleWrap?: (columnId: string) => void;
  onResizeToFit?: (columnId: string) => void;
  onRemoveColumn?: (columnId: string) => void;
  // Alias functionality
  hasAlias?: boolean;
  onSetAlias?: (columnId: string) => void;
}

export function ColumnActionsMenu({
  column,
  isPinned,
  isWrapped = false,
  currentFilter,
  updateFilter,
  clearFilter,
  onTogglePin,
  onToggleWrap,
  onResizeToFit,
  onRemoveColumn,
  hasAlias,
  onSetAlias,
}: ColumnActionsMenuProps) {
  // Control filter popover via state so selecting the menu item opens it.
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  // Get column capabilities with defaults applied
  const capabilities = getColumnCapabilities(column);

  // Check if this is a metric column that supports aliases
  const isMetricColumn = column.columnType === "metric";
  const showAliasOption = isMetricColumn && onSetAlias;

  return (
    <div className="relative inline-flex">
      {/* Dropdown trigger (kebab) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            ref={buttonRef}
            data-testid={`column-menu-${column.key}`}
            aria-label="Column options"
          >
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[8rem]">
          {onTogglePin && (
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onSelect={(e) => {
                e.preventDefault();
                onTogglePin(column.key.toString());
              }}
            >
              <Pin
                className={cn(
                  "h-3 w-3 transition-transform",
                  isPinned && "rotate-45",
                )}
              />
              <span>{isPinned ? "Unpin column" : "Pin column"}</span>
            </DropdownMenuItem>
          )}

          {onResizeToFit && (
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onSelect={() => {
                onResizeToFit(column.key.toString());
              }}
            >
              <UnfoldHorizontal className="h-3 w-3" />
              <span>Resize to fit</span>
            </DropdownMenuItem>
          )}

          {onToggleWrap && (
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onSelect={() => {
                onToggleWrap(column.key.toString());
              }}
            >
              <WrapText className="h-3 w-3" />
              <span>{isWrapped ? "Unwrap content" : "Wrap content"}</span>
            </DropdownMenuItem>
          )}

          {/* Display alias option - only for metric columns */}
          {showAliasOption && (
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onSelect={() => {
                onSetAlias(column.key.toString());
              }}
            >
              <Edit3 className="h-3 w-3" />
              <span>
                {hasAlias ? "Edit display alias" : "Set display alias"}
              </span>
            </DropdownMenuItem>
          )}

          {/* Remove column option - not available for required columns */}
          {onRemoveColumn && !column.required && (
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer text-destructive"
              onSelect={() => {
                onRemoveColumn(column.key.toString());
              }}
            >
              <X className="h-3 w-3" />
              <span>Remove column</span>
            </DropdownMenuItem>
          )}

          {/* Show separator only if we have items above and below */}
          {(onTogglePin ||
            onResizeToFit ||
            onToggleWrap ||
            showAliasOption ||
            (onRemoveColumn && !column.required)) &&
            capabilities.filterable && <DropdownMenuSeparator />}

          {/* Filter item - only show if column is filterable */}
          {capabilities.filterable && (
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onSelect={() => {
                setFilterOpen(true);
              }}
            >
              <Filter className="h-3 w-3" />
              <span>Filter…</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Second layer popover for filter panel - only render if column is filterable */}
      {capabilities.filterable && (
        <Popover open={filterOpen} onOpenChange={setFilterOpen} modal>
          <PopoverAnchor asChild>
            {/* use the same kebab button as anchor via a sr-only duplicate */}
            <button
              ref={buttonRef}
              className="sr-only absolute bottom-0 right-0"
            />
          </PopoverAnchor>
          <PopoverContent
            side="bottom"
            sideOffset={8}
            align="end"
            className="p-0 border-none bg-transparent shadow-none"
          >
            <FilterPopover
              standalone={false}
              column={column}
              currentFilter={currentFilter}
              onFilterChange={updateFilter}
              onClearFilter={clearFilter}
              onRequestClose={() => setFilterOpen(false)}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
